import React, { useState, useEffect, useContext } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchingExternalLibrary,
  updateMediaUploadProgress,
  uploadMedia,
  initiateMediaUpload,
  invalidateUploadError
} from "actions/media";

import _ from "lodash";
import cx from "classnames";

import S3Upload from "react-s3-uploader/s3upload";
import AuthService from "services/auth.service";
import shortid from "shortid";
import InfiniteScroll from "components/InfiniteScroll";

import { Modal, Button, Form, InputGroup, Dropdown } from "react-bootstrap";
import styles from "./externalMedia.module.scss";

const API_URL = `${process.env.REACT_APP_PAPERFLITE_API_URL}/api/2.0`;

export const ExternalMediaView = ({onChange, mediaLibraryType, orientation, editorSession, target, onSelect, active}) => {
  const dispatch = useDispatch();

  const [searchQuery, setSearchQuery] = useState("");
  const [filterType, setFilterType] = useState({label: "Images", value: "image"});
  const [preview, setPreview] = useState(false);
  const [selectedImage, setSelectedImage] = useState([]);
  const mediaList = useSelector((state) => _.get(state, "media.externalMedia.items")) || [];
  const loading = useSelector((state) => _.get(state, "media.externalMedia.loading", false));
  const loaded = useSelector((state) => _.get(state, "media.externalMedia.loaded", false));
  const loadMore = useSelector((state) => _.get(state, "media.externalMedia.loadMore"));
  const pagination = useSelector((state) => _.get(state, "media.externalMedia.pagination"));
  const hasMoreItems = loadMore && _.get(pagination, "currentPage") < 30;

  const filterTypeOption = [
    {
      label: "Images",
      value: "image",
    },
    {
      label: "Videos",
      value: "video",
    },
  ];

  useEffect(() => {
    if (!loaded && active === 'pexels') {
      const request = {
        mediaType: filterType.value,
        mediaLibraryType: mediaLibraryType,
        orientation: orientation,
        query: searchQuery,
        page: 0
      };

      dispatch(fetchingExternalLibrary(request));
    }
  }, [active]);

  useEffect(() => { 
    if(active == "pexels"){
      handleSearch();

      setSearchQuery("");
      setFilterType({
        label: "Images",
        value: "image",
      });
    }
  }, []);

  const loadMoreMedia = () => {
    let totalPages = 30;
    let currentPage = pagination.currentPage;
    let nextPage = currentPage + 1;

    if (loading || nextPage > totalPages) {
      return;
    }

    const request = {
      mediaType: filterType.value,
      mediaLibraryType: mediaLibraryType,
      orientation: orientation,
      page: nextPage,
      query: searchQuery
    };
    dispatch(fetchingExternalLibrary(request));
  };

  const handleSearch = () => {
    var request = {
      mediaType: filterType.value,
      mediaLibraryType: mediaLibraryType,
      orientation: orientation,
      query: searchQuery,
      page: 0
    };

    dispatch(fetchingExternalLibrary(request));
  };

  const handlePreview = (item) => {
    setSelectedImage(item);
    setPreview(true);
  };

  const onClose = () => {
    setPreview(false);
    dispatch(invalidateUploadError());
  }

  return (
    <div className={styles.externalMedia}>
      <div className={cx(styles.externalMediaHeader, styles.grid)}>
        <div className={styles.search}>
          <InputGroup className={styles.searchBox}>
            <Form.Control
              className={styles.searchInput}
              placeholder="Search in Pexels"
              aria-label="Search in Pexels"
              aria-describedby="Pexel search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleSearch();
                }
              }}
            />
            <InputGroup.Text
              className={styles.searchIcon}
              id="basic-addon1"
              onClick={handleSearch}
            >
              <i className="cleverstory-search" />
            </InputGroup.Text>
          </InputGroup>
        </div>
        <div className={styles.sortMedia}>
          <div className={styles.sortbyLabel}>
            <Dropdown className={styles.storboardTypeDropdown}>
              <Dropdown.Toggle id="dropdown-custom-components">
                {filterType.label}
              </Dropdown.Toggle>
              <Dropdown.Menu>
                {filterTypeOption.map((option, index) => {
                  return (
                    <Dropdown.Item
                      active={option.value == filterType.value}
                      onClick={() => setFilterType(option)}
                      key={index}
                      eventKey={index}
                    >
                      {option.label}
                    </Dropdown.Item>
                  );
                })}
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </div>
      </div>

      <div className={styles.externalMediaGallery}>
        {loading && mediaList.length == 0 && <p className={styles.loading}>Fetching...</p>}

        {!loading && mediaList.length == 0 && searchQuery != "" && (
          <>
            <div className={styles.errorMessageContent}>
              <p className={styles.errorMessageTitle}>
                We couldn’t find anything for "{searchQuery}".
                <br /> Try to refine your search.
              </p>
              <button
                className={styles.errorMessageButton}
                onClick={()=>{setSearchQuery("")}}
              >
                Clear Search
              </button>
            </div>
          </>
        )}

        {mediaList.length != 0 && (
          <InfiniteScroll
            className={styles.scrollable}
            loading={loading}
            hasMore={hasMoreItems}
            loadMore={() => loadMoreMedia()}
            pageStart={1}
            threshold={250}
          >
            <div className={styles.grid}>
              {mediaList.map((media, index) => {
                return (
                  <ExternalMedia
                    key={index}
                    media={media}
                    onPreviewClick={() => handlePreview(media)}
                  />
                );
              })}
            </div>
          </InfiniteScroll>
        )}
      </div>

      {preview && (
        <PreviewExternalMedia
          show={preview}
          onSelect={onSelect}
          selectedMedia={selectedImage}
          onClose={onClose}
          editorSession={editorSession}
          target={target}
          onChange={onChange}
        />
      )}
    </div>
  );
};

export const ExternalMedia = ({ media, selected, onClick, onPreviewClick }) => {
  const type = media.mediaType;

  return (
    <div
      className={cx(styles.media, { [styles.selected]: selected })}
      onClick={onClick}
    >
      {type == "image" && (
        <div className={styles.icon} onClick={onPreviewClick}>
          <img src={media.thumbnailUrl} alt="Media Thumbnail" />
          <div className={styles.overlay}></div>
        </div>
      )}

      {type == "video" && (
        <div className={styles.icon} onClick={onPreviewClick}>
          <video name="media" className={styles.videoPlayer} muted>
            <source src={media.imageUrl} />
          </video>
          <div className={styles.overlay}></div>
        </div>
      )}
    </div>
  );
};

export const PreviewExternalMedia = ({show, onSelect, onClose, selectedMedia, target}) => {
  const dispatch = useDispatch();

  const storyboard = useSelector((state) => _.get(state.storyboards, "storyboard.item") || null);
  const processing = useSelector((state) => _.get(state.media.externalMedia, "processing", false));
  const uploadingFail = useSelector((state) => _.get(state.media.externalMedia, "uploadError", false));
  const [selected, setSelected] = useState([]);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (selected.length != 0) {
      onSelect(selected);
      onClose();
    }
  }, [selected]);

  useEffect(() => {
    if(processing) {
      setUploading(true);
    }
  }, [processing]);

  const handleMediaUpload = async (item) => {
    setUploading(!uploading);

    const url = item.imageUrl;
    const mediaType = item.mediaType;
    const urlParts = url.split("/");
    let file = item.imageUrl;
    const mediaWithExtension = urlParts[urlParts.length - 1];
    const [mediaWithExtensionWithoutQuery] = mediaWithExtension.split("?");
    const lastDotIndex = mediaWithExtensionWithoutQuery.lastIndexOf(".");
    const mediaName = mediaWithExtensionWithoutQuery.substring(0, lastDotIndex);
    const extension = mediaWithExtensionWithoutQuery.substring(lastDotIndex + 1);
    
    let finalExtension = extension;

    if (finalExtension === "jpg") {
      finalExtension = "jpeg";
    }

    const fileNameWithExtension = `${mediaName}.${finalExtension}`;
    const type = `${mediaType}/${finalExtension}` || "image/jpeg";

    if (mediaType == "image") {
      try {
        const image = new Image();
        image.onload = () => {
          const width = image.width;
          const height = image.height;
          const fileName = fileNameWithExtension;
          const contentType = type;
          initiateUpload(url, width, height, contentType, fileName);
        };
        image.onerror = (error) => {
          console.error(error);
        };
        image.src = url;
      } catch (error) {
        console.error(error);
      }
    }

    if (mediaType == "video" && typeof file === "string") {
      try {
        let width, height;
        const video = document.createElement("video");
        video.onloadedmetadata = () => {
          width = video.videoWidth;
          height = video.videoHeight;
          const fileName = fileNameWithExtension;
          const contentType = type;
          initiateUpload(url, width, height, contentType, fileName);
        };
        video.onerror = (error) => {
          console.error(error);
        };
        video.src = file;
      } catch (error) {
        console.error(error);
      }
    }
  };

  const blobToFile = (blob, fileName, contentType, width, height) => {
    const options = {
      type: contentType,
      width: width,
      height: height,
      fileName: fileName,
    };

    try {
      const file = new File([blob], fileName, options);
      return file;
    } catch (error) {
      console.error(error);
      return null;
    }
  };

  const onComplete = (info, file) => {
    const request = {
      fileName: file.name,
      contentType: file.type,
      signedUrl: info.signedUrl,
      width: file.width,
      height: file.height,
      storyboard: _.get(storyboard, 'id'),
      target: target,
    };

    dispatch(uploadMedia(file.id, request, (response) => {
        setSelected([response]);
      }));
  };

  const handleUploadProgress = (progress, file) => {
    dispatch(updateMediaUploadProgress(file.id, progress));
  };
  
  const onError = (error) => {
    console.log(error);
  };

  const handleFileCheck = (file, next) => {
    if (file.type === "video/mp4" && file.size / 1048576 > 15) {
      console.log("video size is maximum");
    } else {
      next(file);
    }
  };

  const initiateUpload = async (imageUrl, width, height, contentType, fileName) => {
    try {
      const response = await fetch(imageUrl);

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const blob = await response.blob();
      const file = blobToFile(blob, fileName, contentType, width, height);
      file.id = shortid();
      file.width = width;
      file.height = height;

      var s3Uploader = new S3Upload({
        signingUrl: "/file_upload/initiate",
        signingUrlMethod: "POST",
        signingUrlHeaders: {
          Authorization: `Bearer ${AuthService.getAccessToken()}`
        },
        server: API_URL,
        scrubFilename: (filename) => {
          var name = filename.replace(/[^\w\d_\-.\s]+/gi, "");
          return name;
        },
        signingUrlQueryParams: { originalName: encodeURIComponent(file.name) },
        contentDisposition: "auto",
        preprocess: handleFileCheck,
        uploadRequestHeaders: {},
        onFinishS3Put: onComplete,
        onProgress: handleUploadProgress,
        onError: onError,
        files: [file],
      });
      const upload = {
        id: s3Uploader.files[0].id,
        name: s3Uploader.files[0].name,
        type: s3Uploader.files[0].type,
        size: s3Uploader.files[0].size / 1048576,
        progress: 0,
        instance: s3Uploader,
      };

      dispatch(initiateMediaUpload(upload));
    } catch (error) {
      console.error(error);
    }
  };

  const fileUrl = selectedMedia.imageUrl;
  const urlParts = fileUrl.split("/");
  const mediaWithExtension = urlParts[urlParts.length - 1];
  const [mediaWithExtensionWithoutQuery] = mediaWithExtension.split("?");
  const lastDotIndex = mediaWithExtensionWithoutQuery.lastIndexOf(".");
  const mediaName = mediaWithExtensionWithoutQuery.substring(0, lastDotIndex);
  const extension = mediaWithExtensionWithoutQuery.substring(lastDotIndex + 1);

  return (
    <div className={styles.previewMedia}>
      <Modal show={show} onHide={onClose} size="xl" className={styles.dialog}>
        <Modal.Header closeButton>
          <div className={styles.mediaLibraryHeader}>
            {mediaName}.{extension}
          </div>
        </Modal.Header>
        <Modal.Body>
          {selectedMedia.mediaType == "image" && (
            <div className={styles.externalImage}>
              <div className={styles.imageIcon}>
                <img
                  className={styles.image}
                  src={selectedMedia.thumbnailUrl}
                />
              </div>
            </div>
          )}

          {selectedMedia.mediaType == "video" && (
            <div className={styles.externalVideo}>
              <video
                name="media"
                className={styles.videoPlayer}
                autoPlay
                muted
                loop
              >
                <source src={selectedMedia.imageUrl} />
              </video>
            </div>
          )}

          {uploadingFail && (
            <div className={styles.errorMessage}>
              The video size should be less than 15mb
              <Button
                variant="info"
                onClick={() => onClose()}
                className={styles.closeIcon}
              >
                <i className="cleverstory-close" />
              </Button>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            size="sm"
            disabled={uploading}
            variant="primary"
            className={styles.uploadButton}
            onClick={() => handleMediaUpload(selectedMedia)}
          >
            {uploading ? "Processing..." : "Choose Media"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};
